import { useEffect, useState } from 'react';
import { getUsers } from '../../../api';
import useRouteStore from '../../../hooks/useRouteStore';
import Table from '../../../common/Table';

const roleBadge = {
  admin:   { label: 'Admin',    className: 'bg-red-100 text-red-700' },
  donor:   { label: 'Donador',  className: 'bg-blue-100 text-blue-700' },
  user:    { label: 'Usuario',  className: 'bg-green-100 text-green-700' },
  default: { label: 'Usuario',  className: 'bg-gray-100 text-gray-600' },
};

const columns = [
  {
    key: 'name',
    title: 'Nombre',
    sortable: true,
    mutator: (_, row) => `${row.name} ${row.lastname}`,
  },
  {
    key: 'email',
    title: 'Correo',
    sortable: true,
  },
  {
    key: 'role',
    title: 'Rol',
    sortable: true,
    width: '90px',
    mutator: (v) => {
      const { label, className } = roleBadge[v] ?? roleBadge.default;
      return (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${className}`}>
          {label}
        </span>
      );
    },
  },
  {
    key: 'verified',
    title: 'Verificado',
    width: '90px',
    mutator: (v) =>
      v ? (
        <span className='inline-flex items-center gap-1 text-xs font-semibold text-green-700'>
          <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
          </svg>
          Sí
        </span>
      ) : (
        <span className='inline-flex items-center gap-1 text-xs font-semibold text-gray-400'>
          <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
          </svg>
          No
        </span>
      ),
  },
  {
    key: 'lastLogin',
    title: 'Último acceso',
    sortable: true,
    mutator: (v) => (v ? new Date(v).toLocaleDateString() : '—'),
  },
  {
    key: 'last_payment',
    title: 'Último pago',
    sortable: true,
    mutator: (v) => (v ? new Date(v).toLocaleDateString() : '—'),
  },
  {
    key: '_id',
    title: '',
    button: { link: '/administracion/usuarios/:_id', text: 'Ver' },
    width: '50px',
  },
];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [maxPage, setMaxPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [page, setPage] = useState(1);

  const setRoute = useRouteStore((state) => state.setRoute);

  useEffect(() => {
    setRoute([
      { name: 'Inicio', url: '/administracion' },
      { name: 'Usuarios', url: '/administracion/usuarios' },
    ]);
  }, []);

  useEffect(() => {
    setLoading(true);
    getUsers({ page, search: query, role: roleFilter, sortBy, sortOrder }).then((res) => {
      setUsers(res.data.users);
      setMaxPage(res.data.maxPage);
      setLoading(false);
    });
  }, [page, query, roleFilter, sortBy, sortOrder]);

  const handleSearch = (q) => {
    setQuery(q);
    setPage(1);
  };

  const handleRoleFilter = (role) => {
    setRoleFilter(role);
    setPage(1);
  };

  // Table uses 0-based pages; API uses 1-based
  const handleChangePage = (zeroBasedPage) => {
    setPage(zeroBasedPage + 1);
  };

  const handleSort = (key, dir) => {
    setSortBy(key ?? '');
    setSortOrder(dir ?? '');
    setPage(1);
  };

  return (
    <div className='font-montserrat w-full'>
      <h1 className='font-semibold text-xl pb-1 mb-3 border-b-2'>Usuarios</h1>

      {/* Role filter */}
      <div className='flex gap-2 mb-3 flex-wrap'>
        {[
          { value: '',       label: 'Todos' },
          { value: 'admin',  label: 'Admin' },
          { value: 'donor',  label: 'Donador' },
          { value: 'user',   label: 'Usuario' },
        ].map(({ value, label }) => (
          <button
            key={value}
            onClick={() => handleRoleFilter(value)}
            className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors border ${
              roleFilter === value
                ? 'bg-red-1 text-white border-red-1'
                : 'bg-white text-gray-600 border-gray-200 hover:border-red-1 hover:text-red-1'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <Table
        ajax
        data={users}
        columns={columns}
        loading={loading}
        elementsPerPage={20}
        maxPage={maxPage}
        onChangePage={handleChangePage}
        onSort={handleSort}
        tools={['search']}
        onSearch={handleSearch}
      />
    </div>
  );
};

export default Users;
